<script setup lang="ts">
import { ref } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { inventoryApi, type InventoryBalance, type PlaceFilter } from '@/api/inventory';
import { apiErrorText } from '@/utils/api-errors';

const props = defineProps<{ place: PlaceFilter; balance: InventoryBalance }>();
const emit = defineEmits<{ close: [] }>();

const queryClient = useQueryClient();
const minimum = ref<number | ''>(props.balance.minQuantity ?? '');
const error = ref('');

const save = useMutation({
  mutationFn: () => inventoryApi.setMinimum({
    inventoryItemId: props.balance.inventoryItemId,
    ...props.place,
    minQuantity: minimum.value === '' ? null : minimum.value,
  }),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['inventoryBalances'] });
    queryClient.invalidateQueries({ queryKey: ['inventoryAlerts'] });
    emit('close');
  },
  onError: (failure) => {
    error.value = apiErrorText(failure, 'No se pudo guardar el mínimo.');
  },
});
</script>
<template>
  <v-card :title="`Stock mínimo · ${balance.itemName}`">
    <v-card-text class="pt-5">
      <v-text-field
        v-model.number="minimum"
        type="number"
        min="0"
        :label="`Mínimo (${balance.humanUnitOfMeasure.toLowerCase()})`"
        hint="Al llegar a este stock se avisa por correo una vez. Déjalo vacío para quitarlo."
        persistent-hint
        variant="outlined"
        density="compact"
        clearable
      />
      <v-alert
        v-if="error"
        type="error"
        variant="tonal"
        class="mt-3"
        :text="error"
      />
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn @click="emit('close')">
        Cancelar
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        :loading="save.isPending.value"
        @click="save.mutate()"
      >
        Guardar
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
